import type { TrexApiProxy } from "../trex-api/trex-api-proxy";
import {
  Api,
  type ProjectConfirmRequest,
  type ProjectCustomActionRequest,
  type ProjectPatchProposalRequest,
  type ProjectResearchRequest,
  type ProjectSubmitProposalRequest,
  type RequestParams,
} from "./regis-api";

const JSON_FORMAT = { format: "json" as const };

/**
 * 后端统一响应包：{ success: boolean, code: string, message: string, obj: ... }
 * `code !== "0000"` 即视为业务错误。这里不直接弹 UI（api 层不应依赖 UI 包），
 * 而是往 `window` 广播一个原生事件，由 `@trex-2b/ui` 的 Toast 模块自行监听并弹出提示
 * （见 packages/ui/src/toast/toast.tsx 里同名事件常量）。两边不产生任何 import 依赖。
 */
const BUSINESS_ERROR_EVENT = "trex-regis-business-error";
const SUCCESS_CODE = "0000";

function emitIfBusinessError(error: unknown): void {
  if (typeof window === "undefined") {
    return;
  }
  const body = (error as { error?: { code?: string } })?.error;
  if (!body || typeof body.code !== "string" || body.code === SUCCESS_CODE) {
    return;
  }
  window.dispatchEvent(new Event(BUSINESS_ERROR_EVENT));
}

class AegisApiProxy {
  private client: InstanceType<typeof Api>;
  private trexProxy: TrexApiProxy;

  constructor(
    client: InstanceType<typeof Api>,
    trexProxy: TrexApiProxy,
    baseUrl?: string,
  ) {
    this.client = client;
    this.trexProxy = trexProxy;
    if (baseUrl !== undefined) {
      this.client.baseUrl = baseUrl;
    }
  }

  private async requestWithAuth<T>(request: () => Promise<T>): Promise<T> {
    if (!this.trexProxy.token) {
      throw new Error("No token available");
    }

    try {
      return await request();
    } catch (error: unknown) {
      const status = (error as { status?: number })?.status;
      if (status === 401 && this.trexProxy.refreshToken) {
        await this.trexProxy.refreshAccessToken();
        try {
          return await request();
        } catch (retryError: unknown) {
          if ((retryError as { status?: number })?.status === 401) {
            this.trexProxy.clearToken();
          }
          emitIfBusinessError(retryError);
          throw retryError;
        }
      }
      emitIfBusinessError(error);
      throw error;
    }
  }

  private withAuth(params: RequestParams = {}): RequestParams {
    return {
      ...JSON_FORMAT,
      ...params,
      headers: this.trexProxy.getHeaders(params),
    };
  }

  get v1() {
    return {
      research: (data: ProjectResearchRequest, params: RequestParams = {}) =>
        this.requestWithAuth(() =>
          this.client.v1.research(data, this.withAuth(params)),
        ),

      materialize: (
        sessionId: string,
        query: { handle: string },
        params: RequestParams = {},
      ) =>
        this.requestWithAuth(() =>
          this.client.v1.materialize(sessionId, query, this.withAuth(params)),
        ),

      confirm: (
        sessionId: string,
        query: { handle: string },
        data: ProjectConfirmRequest,
        params: RequestParams = {},
      ) =>
        this.requestWithAuth(() =>
          this.client.v1.confirm(sessionId, query, data, this.withAuth(params)),
        ),

      submitProposal: (
        data: ProjectSubmitProposalRequest,
        params: RequestParams = {},
      ) =>
        this.requestWithAuth(() =>
          this.client.v1.submitProposal(data, this.withAuth(params)),
        ),

      cohortsRecommend: (
        query: { action_template_name: string },
        params: RequestParams = {},
      ) =>
        this.requestWithAuth(() =>
          this.client.v1.cohortsRecommend(query, this.withAuth(params)),
        ),

      customActionRequest: (
        data: ProjectCustomActionRequest,
        params: RequestParams = {},
      ) =>
        this.requestWithAuth(() =>
          this.client.v1.customActionRequest(data, this.withAuth(params)),
        ),

      recommendActions: (params: RequestParams = {}) =>
        this.requestWithAuth(() =>
          this.client.v1.recommendActions(this.withAuth(params)),
        ),

      deleteProposal: (id: string, params: RequestParams = {}) =>
        this.requestWithAuth(() =>
          this.client.v1.deleteProposal(id, this.withAuth(params)),
        ),

      patchProposal: (
        id: string,
        data: ProjectPatchProposalRequest,
        params: RequestParams = {},
      ) =>
        this.requestWithAuth(() =>
          this.client.v1.patchProposal(id, data, this.withAuth(params)),
        ),

      getProject: (
        sessionId: string,
        query: { handle: string },
        params: RequestParams = {},
      ) =>
        this.requestWithAuth(() =>
          this.client.v1.getProject(sessionId, query, this.withAuth(params)),
        ),

      stream: (
        sessionId: string,
        query: { handle: string },
        params: RequestParams = {},
      ) =>
        this.requestWithAuth(() =>
          this.client.v1.stream(sessionId, query, this.withAuth(params)),
        ),

      listMyProposals: (params: RequestParams = {}) =>
        this.requestWithAuth(() =>
          this.client.v1.listMyProposals(this.withAuth(params)),
        ),

      getMyProject: (params: RequestParams = {}) =>
        this.requestWithAuth(() =>
          this.client.v1.getMyProject(this.withAuth(params)),
        ),

      deleteMyProject: (params: RequestParams = {}) =>
        this.requestWithAuth(() =>
          this.client.v1.deleteMyProject(this.withAuth(params)),
        ),

      cohortsLibrary: (params: RequestParams = {}) =>
        this.requestWithAuth(() =>
          this.client.v1.cohortsLibrary(this.withAuth(params)),
        ),

      cohortDetail: (name: string, params: RequestParams = {}) =>
        this.requestWithAuth(() =>
          this.client.v1.cohortDetail(name, this.withAuth(params)),
        ),

      actionsLibrary: (params: RequestParams = {}) =>
        this.requestWithAuth(() =>
          this.client.v1.actionsLibrary(this.withAuth(params)),
        ),
    };
  }
}

export { AegisApiProxy };
